import MuiBox from '@mui/material/Box';
import { forwardRef, type AriaRole, type ElementType, type ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';

export interface BoxProps {
  sx?: SxProps<Theme>;
  component?: ElementType;
  // ARIA role — a standard DOM attribute, not MUI-specific. Lets composition
  // components (e.g. EmptyState's role="status") stay on the façade instead of
  // dropping to @mui/material/Box just to set it (ADR 0004).
  role?: AriaRole;
  // id / aria-label / aria-labelledby / tabIndex: standard DOM attributes (like role above).
  // Together they turn a Box into a keyboard-operable, accessibly-named region — e.g. an internally
  // scrolling log — and give it a stable anchor id for a toggle's aria-controls.
  //
  // `aria-labelledby` is the one to reach for when the name is ALREADY ON SCREEN: it points at that
  // text instead of restating it, so the two can never drift apart the way a duplicated
  // `aria-label` does the first time somebody edits only the visible one.
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  tabIndex?: number;
  children?: ReactNode;
}

// forwardRef: nothing can put focus INTO a region without a handle on it — e.g. landing focus on
// the heading a navigation just changed. A fixed id + getElementById would put a DOM query in a view.
export const Box = forwardRef<HTMLElement, BoxProps>(function Box(
  { sx, component, role, id, tabIndex, children, ...rest },
  ref,
) {
  const ariaLabel = rest['aria-label'];
  const ariaLabelledBy = rest['aria-labelledby'];
  // Forward optionals only when set: the project runs with
  // exactOptionalPropertyTypes, which rejects prop={undefined}.
  return (
    <MuiBox
      ref={ref}
      {...(sx !== undefined ? { sx } : {})}
      {...(component !== undefined ? { component } : {})}
      {...(role !== undefined ? { role } : {})}
      {...(id !== undefined ? { id } : {})}
      {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
      {...(ariaLabelledBy !== undefined ? { 'aria-labelledby': ariaLabelledBy } : {})}
      {...(tabIndex !== undefined ? { tabIndex } : {})}
    >
      {children}
    </MuiBox>
  );
});
